import {
  Competition as GqlCompetition,
  type Stat,
} from '@statmuse/core/../graphql/graphql'
import { assertUnmatched } from '@statmuse/core/util'

export type Competition =
  | 'Bundesliga'
  | 'PremierLeague'
  | 'LaLiga'
  | 'Ligue1'
  | 'SerieA'
  | 'ChampionsLeague'

export const competitionFromGameraLeagueParameter = (
  gameraLeagueParameter: string,
): Competition | undefined => {
  switch (gameraLeagueParameter) {
    case 'epl':
      return 'PremierLeague'
    case 'lal':
      return 'LaLiga'
    case 'bun':
      return 'Bundesliga'
    case 'ser':
      return 'SerieA'
    case 'lig':
      return 'Ligue1'
    case 'ucl':
      return 'ChampionsLeague'
    default:
      return undefined
  }
}

export const competitionFromGqlType = (
  gqlCompetition: GqlCompetition,
): Competition => {
  switch (gqlCompetition) {
    case GqlCompetition.Bun:
      return 'Bundesliga'
    case GqlCompetition.Epl:
      return 'PremierLeague'
    case GqlCompetition.Lal:
      return 'LaLiga'
    case GqlCompetition.Lig:
      return 'Ligue1'
    case GqlCompetition.Ser:
      return 'SerieA'
    case GqlCompetition.Ucl:
      return 'ChampionsLeague'
    default:
      assertUnmatched(gqlCompetition)
  }
}

export const competitionToName = (competition: Competition) => {
  switch (competition) {
    case 'Bundesliga':
      return 'Bundesliga'
    case 'PremierLeague':
      return 'PL'
    case 'LaLiga':
      return 'LaLiga'
    case 'SerieA':
      return 'Serie A'
    case 'Ligue1':
      return 'Ligue 1'
    case 'ChampionsLeague':
      return 'Champions League'
    default:
      assertUnmatched(competition)
  }
}

export type StatMap = Record<string, Stat>

interface BaseStatMetadata {
  any?: (s: StatMap) => boolean
  color?: (s: StatMap) => 'red' | 'orange' | 'green' | 'blue' | undefined
  display?: (s: StatMap) => string
  shortLabel?: string
  label: string
  statKey?: string
}

const hasPositiveValue = (stat: Stat | undefined) => (stat?.value ?? 0) > 0

export const ratingColor = (
  display: string,
  hasHighestValue?: boolean,
): 'red' | 'orange' | 'green' | 'blue' | undefined => {
  // The color-coding is intentionally based on the (rounded) display value,
  // not the actual numerical value, so that e.g., 7.00 is always green
  // even if the exact rating is 6.995
  const rating = parseInt(display)

  return rating && !isNaN(rating) && rating <= 10
    ? hasHighestValue && hasHighestValue > 0
      ? 'blue'
      : rating >= 7
        ? 'green'
        : rating >= 5
          ? 'orange'
          : 'red'
    : undefined
}

const roundedPercent = (value: number) => `${Math.round(value)}%`

const defineStat = <T extends BaseStatMetadata>(
  metadata: T,
): BaseStatMetadata & Required<Pick<BaseStatMetadata, 'any' | 'display'>> => {
  const defaultAny = (stat: BaseStatMetadata, s: StatMap): boolean => {
    return !!stat.statKey && hasPositiveValue(s[stat.statKey])
  }

  const defaultDisplay = (stat: BaseStatMetadata, s: StatMap): string => {
    return (stat.statKey ? s[stat.statKey]?.display : undefined) ?? '-'
  }

  return {
    ...metadata,
    any: metadata.any || ((s: StatMap) => defaultAny(metadata, s)),
    display: metadata.display || ((s: StatMap) => defaultDisplay(metadata, s)),
  }
}

const fcStatMetadataDefinitions = {
  Aerials: defineStat({
    display: (s: StatMap) => {
      const aerialsWon = s.AerialsWon
      const aerialsLost = s.AerialsLost

      return aerialsWon && aerialsLost
        ? `${aerialsWon.display}-${aerialsLost.display}`
        : '-'
    },
    label: 'Aerial W-L',
    any: (s: StatMap) =>
      hasPositiveValue(s.AerialsWon) || hasPositiveValue(s.AerialsLost),
  }),
  Assists: defineStat({
    statKey: 'Assists',
    shortLabel: 'A',
    label: 'Assists',
  }),
  BigChancesCreated: defineStat({
    label: 'BCC',
    statKey: 'BigChancesCreated',
  }),
  BigChancesMissed: defineStat({
    label: 'BCM',
    statKey: 'BigChancesMissed',
  }),
  ChancesCreated: defineStat({
    label: 'Chance\nCreated',
    statKey: 'ChancesCreated',
  }),
  CleanSheets: defineStat({
    shortLabel: 'CS',
    label: 'Clean Sheets',
    statKey: 'CleanSheets',
  }),
  Clearances: defineStat({
    label: 'Clearance',
    statKey: 'Clearances',
  }),
  ConversionPercent: defineStat({
    label: 'Conv%',
    statKey: 'ConversionRate',
  }),
  CrossesAttempted: defineStat({
    label: 'Crosses Attempted',
    statKey: 'CrossesAttempted',
  }),
  CrossesAndPercentage: defineStat({
    label: 'Cross%',
    display: (s: StatMap) => {
      const crossesCompleted = s.CrossesCompleted
      const crossesAttempted = s.CrossesAttempted
      const crossingPercentage = s.CrossingPercentage

      return crossesCompleted && crossesAttempted && crossesompletionPercentage
        ? `${crossesCompleted.display}/${crossesAttempted.display} (${roundedPercent(crossingPercentage.value)})`
        : ''
    },
    any: (s: StatMap) => hasPositiveValue(s.DribblesAttempted),
  }),
  DribblesAttempted: defineStat({
    label: 'Dribbles Attempted',
    statKey: 'DribblesAttempted',
  }),
  DribblesAndPercentage: defineStat({
    label: 'Dribble%',
    display: (s: StatMap) => {
      const dribblesCompleted = s.DribblesCompleted
      const dribblesAttempted = s.DribblesAttempted
      const dribblePercentage = s.DribblesCompletionPercentage

      return dribblesCompleted && dribblesAttempted && dribblePercentage
        ? `${dribblesCompleted.display}/${dribblesAttempted.display} (${roundedPercent(dribblePercentage.value)})`
        : ''
    },
    any: (s: StatMap) => hasPositiveValue(s.DribblesAttempted),
  }),
  DribblePercentage: defineStat({
    label: 'Dribble%',
    statKey: 'DribblesCompletionPercentage',
  }),
  Duels: defineStat({
    label: 'Duel\nW-L',
    display: (s: StatMap) => {
      const duelsWon = s.DuelsWon
      const duelsLost = s.DuelsLost

      return duelsWon && duelsLost
        ? `${duelsWon.display}-${duelsLost.display}`
        : '-'
    },
    any: (s: StatMap) =>
      hasPositiveValue(s.DuelsWon) || hasPositiveValue(s.DuelsLost),
  }),
  ErrorsLeadingToShot: defineStat({
    label: 'Error Led To Shot',
    statKey: 'ErrorsLeadingToShot',
  }),
  ErrorsLeadingToGoal: defineStat({
    label: 'Error Led To Goal',
    statKey: 'ErrorsLeadingToGoal',
  }),
  FoulsCommitted: defineStat({
    label: 'Foul',
    statKey: 'FoulsCommitted',
  }),
  FoulsDrawn: defineStat({
    label: 'Fouled',
    statKey: 'FoulsDrawn',
  }),
  Goals: defineStat({
    shortLabel: 'G',
    label: 'Goals',
    statKey: 'Goals',
  }),
  GoalsAgainst: defineStat({
    shortLabel: 'GC',
    label: 'Goals Conceded',
    statKey: 'GoalsAgainst',
  }),
  Interceptions: defineStat({
    label: 'Interceptions',
    statKey: 'Interceptions',
  }),
  LongBalls: defineStat({
    label: 'Long Pass',
    statKey: 'LongBalls',
  }),
  Matches: defineStat({
    shortLabel: 'M',
    label: 'Matches',
    statKey: 'GamesPlayed',
  }),
  MinutesPlayed: defineStat({
    shortLabel: 'MIN',
    label: 'Minutes',
    statKey: 'MinutesPlayed',
  }),
  Offsides: defineStat({
    label: 'Offside',
    statKey: 'Offsides',
  }),
  PassesAndPercentage: defineStat({
    label: 'Pass%',
    display: (s: StatMap) => {
      const passesCompleted = s.PassesCompleted
      const passesAttempted = s.PassesAttempted
      const passCompletionPercentage = s.PassCompletionPercentage

      return passesCompleted && passesAttempted && passCompletionPercentage
        ? `${passesCompleted.display}/${passesAttempted.display} (${roundedPercent(passCompletionPercentage.value)})`
        : ''
    },
    any: (s: StatMap) => hasPositiveValue(s.PassesAttempted),
  }),
  PassesAttempted: defineStat({
    label: 'Passes Attempted',
    statKey: 'PassesAttempted',
  }),
  Passes: defineStat({
    label: 'Passes',
    statKey: 'PassesCompleted',
  }),
  PenaltySaves: defineStat({
    shortLabel: 'PK SV',
    label: 'Penalty Saves',
    statKey: 'PenaltiesSaved',
  }),
  PossessionsLost: defineStat({
    label: 'Poss\nLost',
    statKey: 'TotalPossessionsLost',
  }),
  Rating: defineStat({
    color: (s: StatMap) => {
      return s.Rating?.display
        ? ratingColor(s.Rating.display, (s.HighestRated?.value ?? 0) > 0)
        : undefined
    },
    shortLabel: 'RTG',
    label: 'Rating',
    statKey: 'Rating',
  }),
  Recoveries: defineStat({
    label: 'Recoveries',
    statKey: 'BallRecoveries',
  }),
  RedCards: defineStat({
    statKey: 'RedCards',
    label: 'Red\nCard',
  }),
  Saves: defineStat({
    shortLabel: 'SV',
    label: 'Saves',
    statKey: 'Saves',
  }),
  ShotsAndPercentage: defineStat({
    label: 'Shot%',
    statKey: 'ShotPercentage',
    display: (s: StatMap) => {
      const shotsOnTarget = s.ShotsOnTarget
      const shots = s.Shots
      const shotPercentage = s.ShotPercentage

      return shots && shotsOnTarget && shotPercentage
        ? `${shotsOnTarget.display}/${shots.display} (${roundedPercent(shotPercentage.value)})`
        : '-'
    },
    any: (s: StatMap) => hasPositiveValue(s.Shots),
  }),
  Starts: defineStat({
    label: 'Starts',
    statKey: 'GamesStarted',
  }),
  Tackles: defineStat({
    label: 'Tackle\nMade',
    statKey: 'TacklesMade',
  }),
  ThroughBalls: defineStat({
    label: 'Through Balls',
    statKey: 'ThroughBalls',
  }),
  Touches: defineStat({
    label: 'Touches',
    statKey: 'Touches',
  }),
  TouchesAndTouchesInBox: defineStat({
    label: 'Touches\n(In Box)',
    display: (s: StatMap) => {
      const touches = s.Touches
      const touchesInOpponentBox = s.TouchesInOpponentBox

      return touches && touchesInOpponentBox
        ? `${touches.display} (${touchesInOpponentBox.display})`
        : '-'
    },
    any: (s: StatMap) => hasPositiveValue(s.Touches),
  }),
  xA: defineStat({
    label: 'xA',
    statKey: 'ExpectedAssists',
  }),
  xG: defineStat({
    label: 'xG',
    statKey: 'ExpectedGoals',
  }),
  YellowCards: defineStat({
    label: 'Yellow\nCard',
    statKey: 'YellowCards',
  }),
} as const

export type StatMetadata =
  (typeof fcStatMetadataDefinitions)[keyof typeof fcStatMetadataDefinitions]

export const FcStats: Record<
  keyof typeof fcStatMetadataDefinitions,
  StatMetadata
> = fcStatMetadataDefinitions

export const createStatMap = (values: Array<Stat> | undefined) => {
  return (
    values?.reduce(
      (acc, x) => ({ ...acc, [x.statKey]: { ...x } }),
      {} as StatMap,
    ) ?? ({} as StatMap)
  )
}

export const ratingColorClasses = (
  px: 1 | 1.5 | 2 | 2.5,
  color?: 'red' | 'orange' | 'green' | 'blue' | undefined,
) => {
  return {
    'bg-red fill-red': color === 'red',
    'bg-orange fill-orange': color === 'orange',
    'bg-green fill-green': color === 'green',
    'bg-blue fill-blue': color === 'blue',
    [`rounded-full px-${px}`]: color,
  }
}
